import {
  describeNextControlFlowDigest,
  isNextControlFlowDigest,
} from "@shared/telemetry/next-control-flow";
import posthog from "posthog-js";
import {
  type PosthogExceptionEntry,
  exceptionValue,
  isAdwareWindowInjectionValue,
  isDuckDuckGoFeatureInjectionPosthog,
  isEmptyExceptionNoise,
  isFacebookAndroidPerfLoggerNoise,
  isFacebookIabBridgeException,
  isFirefoxDeadObjectNoise,
  isHardenedErrorAbortNoise,
  isFirefoxIosInjectedGlobalValue,
  isFrozenHistoryAssignmentValue,
  isGoogleTagManagerInjectedTagNoise,
  isInjectedBrowserBridgeMessage,
  isMalwareFetchInjectionPosthog,
  isGoogleMapsRpcTransportNoisePosthog,
  isHostAppInjectedStackOverflowPosthog,
  isNextCurrentScriptInvariantValue,
  isOfficeSafeLinksScannerNoiseValue,
  isPasswordManagerExtensionNoise,
  isPostHogLogsInitNoise,
  isPostHogLogsTimeoutNoise,
  isResourceLoadEventNoisePosthog,
  isResizeObserverLoopNoise,
  isRrwebCrossRealmInstanceofPosthog,
  isStripeConnectScriptLoadFailureValue,
  isTranslatedDomRemoveChildNotFoundError,
  isWalletExtensionInjectedScriptPosthog,
  isWebkitAutofillHandlerValue,
} from "@/lib/error-tracking/noise-filters";
import {
  isDomEventCapturedAsExceptionNoise,
  isPostHogPatchedFetchFailureNoise,
  isPostHogRrwebRecorderNoise,
} from "@/lib/error-tracking/posthog-internal-noise";
import {
  hasDomMutatingExtensionMarkers,
  isTalismanExtensionNoiseValue,
} from "@/lib/error-tracking/client-noise-markers";
import { isRecoverableHydrationErrorValue } from "@/lib/error-tracking/hydration-noise";
import {
  isInterruptedChunkLoad,
  recordInterruptedChunkLoad,
} from "@/lib/error-tracking/interrupted-chunk-load";
import {
  isChunkLoadErrorValue,
  isReloadPendingForThisDocument,
} from "@/lib/chunk-load-recovery";
import { readBreadcrumbs } from "@/lib/error-tracking/breadcrumbs";
import { recordLastEventId } from "@/lib/error-tracking/last-event-id";
import { toPosthogExceptionList } from "@/lib/error-tracking/posthog-exception-types";
import {
  extractConvexFunctionName,
  extractConvexRequestId,
} from "@shared/telemetry/convex-trace";
import { recordConvexRequestId } from "@/lib/telemetry/convex-request-ids";

const BARE_NETWORK_FAILURE_VALUES = new Set([
  "failed to fetch",
  "load failed",
  "network error",
  "networkerror when attempting to fetch resource.",
]);

function countUnattributedNetworkFailure(
  first: PosthogExceptionEntry,
  value: string,
): boolean {
  const unattributed =
    BARE_NETWORK_FAILURE_VALUES.has(value.trim().toLowerCase()) &&
    (first.stacktrace?.frames ?? []).length === 0;
  if (unattributed) {
    posthog.capture("unattributed_network_failure", {
      error_message: value.slice(0, 200),
      pathname: window.location.pathname,
    });
  }
  return unattributed;
}

function countHostAppStackOverflow(list: Array<PosthogExceptionEntry>): boolean {
  const page = {
    origin: window.location.origin,
    userAgent: window.navigator.userAgent,
  };
  if (!isHostAppInjectedStackOverflowPosthog(list, page)) {
    return false;
  }
  const frames = list.flatMap((entry) => entry.stacktrace?.frames ?? []);
  posthog.capture("host_app_stack_overflow", {
    pathname: window.location.pathname,
    frame_count: frames.length,
    named_frame: frames.find((frame) => frame.function)?.function,
  });
  return true;
}

function countHardenedErrorAbort(first: PosthogExceptionEntry): boolean {
  if (!isHardenedErrorAbortNoise(first)) return false;
  posthog.capture("hardened_error_abort", {
    pathname: window.location.pathname,
  });
  return true;
}

function isUnreportedChunkLoad(value: string, type: string): boolean {
  if (isInterruptedChunkLoad(value, type)) {
    recordInterruptedChunkLoad(value);
    return true;
  }
  return isChunkLoadErrorValue(value, type) && isReloadPendingForThisDocument();
}

function isNoiseException(list: Array<PosthogExceptionEntry>): boolean {
  const first = list[0];
  if (!first) return false;
  const value = exceptionValue(first);
  const type = first.type ?? first.$exception_type ?? "";

  if (value === "Script error." || value === "Script error") return true;
  if (isEmptyExceptionNoise(first)) return true;
  if (isResizeObserverLoopNoise(value)) return true;
  if (
    isTranslatedDomRemoveChildNotFoundError(value, type) &&
    hasDomMutatingExtensionMarkers()
  ) {
    return true;
  }
  if (isInjectedBrowserBridgeMessage(value)) return true;
  if (isPostHogLogsInitNoise(first)) return true;
  if (isPostHogLogsTimeoutNoise(first)) return true;
  if (isPostHogRrwebRecorderNoise(first)) return true;
  if (isPostHogPatchedFetchFailureNoise(list)) return true;
  if (isDomEventCapturedAsExceptionNoise(first)) return true;
  if (isWebkitAutofillHandlerValue(value)) return true;
  if (isGoogleMapsRpcTransportNoisePosthog(list)) return true;
  if (isPasswordManagerExtensionNoise(first)) return true;
  if (isFacebookIabBridgeException(first)) return true;
  if (isFacebookAndroidPerfLoggerNoise(list)) return true;
  if (isRrwebCrossRealmInstanceofPosthog(first)) return true;
  if (isNextCurrentScriptInvariantValue(value)) return true;
  if (isFrozenHistoryAssignmentValue(value)) return true;
  if (isFirefoxIosInjectedGlobalValue(value)) return true;
  if (isStripeConnectScriptLoadFailureValue(value)) return true;
  if (isDuckDuckGoFeatureInjectionPosthog(first)) return true;
  if (isWalletExtensionInjectedScriptPosthog(first)) return true;
  if (isAdwareWindowInjectionValue(value)) return true;
  if (isMalwareFetchInjectionPosthog(first)) return true;
  if (isGoogleTagManagerInjectedTagNoise(list)) return true;
  if (isOfficeSafeLinksScannerNoiseValue(value)) return true;
  if (isResourceLoadEventNoisePosthog(first)) return true;
  if (isFirefoxDeadObjectNoise(first)) return true;
  if (countHardenedErrorAbort(first)) return true;
  return isTalismanExtensionNoiseValue(value);
}

export function filterPosthogNoiseExceptions<
  TEvent extends {
    event?: string;
    uuid?: string;
    properties?: Record<string, unknown>;
  } | null,
>(event: TEvent): TEvent | null {
  if (!event || event.event !== "$exception") return event;
  const list = toPosthogExceptionList(event.properties?.["$exception_list"]);
  const first = list[0];
  if (!first) {
    recordLastEventId(event.uuid);
    return withDiagnostics(event, list);
  }
  const value = exceptionValue(first);
  const exceptionType = first.type ?? first.$exception_type ?? "";
  if (isRecoverableHydrationErrorValue(value)) {
    posthog.capture("hydration_recoverable_error", {
      error_message: value.slice(0, 200),
      has_dom_mutating_extension: hasDomMutatingExtensionMarkers(),
      pathname: window.location.pathname,
    });
    return null;
  }
  const controlFlowDigest = isNextControlFlowDigest(value)
    ? value
    : isNextControlFlowDigest(exceptionType)
      ? exceptionType
      : null;
  if (controlFlowDigest !== null) {
    posthog.capture("next_control_flow_client_error", {
      digest: describeNextControlFlowDigest(controlFlowDigest),
      pathname: window.location.pathname,
    });
    return null;
  }
  if (countUnattributedNetworkFailure(first, value)) return null;
  if (isUnreportedChunkLoad(value, exceptionType)) return null;
  if (countHostAppStackOverflow(list)) return null;
  if (isNoiseException(list)) return null;
  recordLastEventId(event.uuid);
  return withDiagnostics(event, list);
}

interface ConvexFailureInException {
  requestId: string;
  functionName: string | undefined;
}

function convexFailureIn(
  list: Array<PosthogExceptionEntry>,
): ConvexFailureInException | undefined {
  for (const entry of list) {
    const value = exceptionValue(entry);
    const requestId = extractConvexRequestId(value);
    if (requestId !== undefined) {
      return { requestId, functionName: extractConvexFunctionName(value) };
    }
  }
  return undefined;
}

function withDiagnostics<
  TEvent extends { properties?: Record<string, unknown> } | null,
>(event: TEvent, list: Array<PosthogExceptionEntry>): TEvent {
  if (!event) return event;
  const trail = readBreadcrumbs();
  const stamped = event.properties?.["convex_request_id"];
  const failure = convexFailureIn(list);
  const convexRequestId = typeof stamped === "string" ? undefined : failure?.requestId;
  const recordedRequestId = typeof stamped === "string" ? stamped : convexRequestId;
  recordConvexRequestId(
    recordedRequestId,
    failure?.requestId === recordedRequestId ? failure?.functionName : undefined,
  );
  if (trail.length === 0 && convexRequestId === undefined) return event;
  return {
    ...event,
    properties: {
      ...event.properties,
      ...(trail.length > 0 && { breadcrumbs: trail }),
      ...(convexRequestId !== undefined && {
        convex_request_id: convexRequestId,
      }),
    },
  };
}
